import { useEffect, useRef, useState } from "preact/hooks";

import type { AssigneeOption } from "../components/AssigneePickerSheet";
import { Snackbar } from "../components/Snackbar";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { useIsDesktop } from "../hooks/useIsDesktop";
import { useProjects } from "../hooks/useProjects";
import { ProjectsView } from "./ProjectsView";

export interface ProjectsScreenProps {
  /** Sprint-017 E7 (ADR-003 §3.3) — the Projects surface is now open to
   * EVERY authenticated role; the SCOPE of the list is server-decided
   * (owner + shared for members/managers, all for admin). `canHandOutWork`
   * no longer gates entry — it drives `canAssignOut` (whether the
   * checklist's assignee chip is interactive). */
  canHandOutWork: boolean;
  isAdmin: boolean;
  /** The signed-in user's id (helper leave / picker self-exclusion).
   * Optional (default `""`) so pre-existing screen tests keep compiling. */
  meId?: string;
  /** People for the assign / "who's helping" pickers (from the team board;
   * empty for a plain member session — see the app.tsx wiring note). */
  roster?: AssigneeOption[];
  onBack: () => void;
}

/**
 * Sprint-008 A1 — the routed `/projects` screen. Lifted verbatim out of
 * `MoreView.tsx`'s former `subView === "projects"` branch — see
 * `AnimalsScreen.tsx`'s docstring for the same "why a verbatim lift"
 * rationale (existing selectors keep matching unchanged).
 *
 * Sprint-008 A2 — same "no 'Back to More' at desktop" treatment as
 * `AnimalsScreen.tsx`; see that file's docstring for the full rationale.
 *
 * Sprint-010 C4 — gains its own local `Snackbar` (same 5s auto-dismiss
 * convention as `AnimalsScreen.tsx`), needed now that `ProjectDetailOverlay`
 * has a real write affordance (the summary/status edit sheet) to confirm.
 */
export function ProjectsScreen({
  canHandOutWork,
  isAdmin,
  meId = "",
  roster = [],
  onBack,
}: ProjectsScreenProps) {
  // Open to every authenticated role now (ADR-003 §3.3) — fetch always.
  const projects = useProjects(true);
  const isDesktop = useIsDesktop();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  useFocusHeadingOnMount(headingRef);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!snackbarMessage) return;
    const timer = setTimeout(() => setSnackbarMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [snackbarMessage]);

  return (
    <section class="view" aria-label="Projects">
      {isDesktop ? (
        <h1 ref={headingRef} tabIndex={-1} class="view-heading">
          Projects
        </h1>
      ) : (
        <div class="more-subheader">
          <button type="button" class="icon-btn" aria-label="Back to More" onClick={onBack}>
            ‹
          </button>
          <h1 ref={headingRef} tabIndex={-1} class="view-heading">
            Projects
          </h1>
        </div>
      )}
      <ProjectsView
        projects={projects}
        isAdmin={isAdmin}
        meId={meId}
        canAssignOut={canHandOutWork}
        roster={roster}
        onNotify={setSnackbarMessage}
      />
      <Snackbar
        state={snackbarMessage ? { message: snackbarMessage } : null}
        onDismiss={() => setSnackbarMessage(null)}
      />
    </section>
  );
}
